// The tabbed body of the Documents screen (Request / My Requests / My Documents),
// shared by the native and web screens.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { useIsFocused, useNavigation } from '@react-navigation/native';
import FeatherIcon from 'react-native-vector-icons/Feather';
import { useAuth } from '../../context/AuthContext';
import { useTheme, ThemeColors } from '../../context/ThemeContext';
import {
  useDocumentCatalog,
  useDocumentFileActions,
  useDocumentLocker,
  useDocumentRequests,
} from '../../hooks/useDocuments';
import {
  CatalogItem,
  DocumentCategory,
  DocumentsTab,
  ParentAdmissionDocument,
  ParentDocument,
  ParentRequestSummary,
  ParentRequestView,
} from '../../types';
import { BorderRadius, FontSize, Spacing } from '../../theme/colors';
import {
  REQUEST_VIEWS,
  catalogCategories,
  emptyRequestsMessage,
  filterCatalog,
  viewCount,
} from '../../utils/documents';
import {
  AdmissionDocumentCard,
  CatalogCard,
  CenteredLoader,
  EmptyState,
  ErrorBanner,
  LockerCard,
  RequestCard,
  useDocStyles,
} from './DocumentUi';
import { RequestFlowModal } from './DocumentForm';

const TABS: { key: DocumentsTab; label: string }[] = [
  { key: 'request', label: 'Request' },
  { key: 'requests', label: 'My Requests' },
  { key: 'documents', label: 'My Documents' },
];

interface Props {
  initialTab?: DocumentsTab;
  /** Native: the content scrolls itself with pull-to-refresh. Web: the page shell scrolls. */
  scrollable: boolean;
  notify: (message: string, type: 'success' | 'error') => void;
}

/**
 * The three tabs of the Documents screen and the request flow, shared by the native
 * and web screens. Requests and the catalogue follow the child chosen in the header;
 * the locker shows the whole family.
 */
export function DocumentsContent({ initialTab, scrollable, notify }: Props) {
  const navigation = useNavigation<any>();
  const { selectedStudent } = useAuth();
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const studentId = selectedStudent?.id;

  const [tab, setTab] = useState<DocumentsTab>(initialTab ?? 'request');
  const [view, setView] = useState<ParentRequestView>('OPEN');
  const [flowItem, setFlowItem] = useState<CatalogItem | null>(null);

  useEffect(() => {
    if (initialTab) setTab(initialTab);
  }, [initialTab]);

  const catalog = useDocumentCatalog(studentId);
  const requests = useDocumentRequests(studentId, view);
  const locker = useDocumentLocker();

  const reloadRequests = requests.reload;
  const reloadLocker = locker.reload;
  const reloadCatalog = catalog.reload;

  // The first download marks a request delivered, so both lists may have changed.
  const onDelivered = useCallback(() => {
    reloadRequests();
    reloadLocker();
  }, [reloadRequests, reloadLocker]);
  const files = useDocumentFileActions(notify, onDelivered);

  // A form belongs to one child: switching child closes it rather than submit for a sibling.
  useEffect(() => {
    setFlowItem(null);
  }, [studentId]);

  // Coming back from a request (after a resubmit or cancel), refresh quietly. Only the
  // unfocused -> focused transition counts, not every change of the reload functions.
  const reloaders = useRef({ reloadRequests, reloadLocker, reloadCatalog });
  reloaders.current = { reloadRequests, reloadLocker, reloadCatalog };
  const isFocused = useIsFocused();
  const wasFocused = useRef(isFocused);
  useEffect(() => {
    if (isFocused && !wasFocused.current) {
      reloaders.current.reloadRequests();
      reloaders.current.reloadLocker();
      reloaders.current.reloadCatalog();
    }
    wasFocused.current = isFocused;
  }, [isFocused]);

  const openRequest = useCallback(
    (requestId: string) => navigation.navigate('DocumentRequestDetail', { requestId }),
    [navigation],
  );

  const refreshing =
    tab === 'request' ? catalog.refreshing : tab === 'requests' ? requests.refreshing : locker.refreshing;
  const refresh = () => {
    if (tab === 'request') catalog.reload('refresh');
    else if (tab === 'requests') requests.reload('refresh');
    else locker.reload('refresh');
  };

  const actionCount = viewCount(requests.counts, 'ACTION_REQUIRED');

  const tabBar = (
    <View style={styles.tabBarWrap}>
      <View style={styles.tabBar} accessibilityRole="tablist">
        {TABS.map(item => {
          const active = item.key === tab;
          return (
            <TouchableOpacity
              key={item.key}
              style={[styles.tabButton, active && styles.tabButtonActive]}
              onPress={() => setTab(item.key)}
              accessibilityRole="tab"
              accessibilityState={{ selected: active }}
              accessibilityLabel={
                item.key === 'requests' && actionCount > 0
                  ? `${item.label}, ${actionCount} need action`
                  : item.label
              }>
              <Text style={[styles.tabLabel, active ? styles.tabLabelActive : styles.tabLabelInactive]} numberOfLines={1}>
                {item.label}
              </Text>
              {item.key === 'requests' && actionCount > 0 && (
                <View style={styles.badge}>
                  <Text style={styles.badgeText}>{actionCount}</Text>
                </View>
              )}
            </TouchableOpacity>
          );
        })}
      </View>
      {!scrollable && (
        <TouchableOpacity
          style={styles.refreshBtn}
          onPress={refresh}
          disabled={refreshing}
          accessibilityRole="button"
          accessibilityLabel="Refresh">
          <FeatherIcon name="refresh-cw" size={16} color={colors.primary} />
        </TouchableOpacity>
      )}
    </View>
  );

  const body = !selectedStudent && tab !== 'documents' ? (
    <EmptyState icon="user-x" title="No child selected" hint="Choose a child from the header to see their documents." />
  ) : tab === 'request' ? (
    <CatalogTab
      studentName={selectedStudent?.name}
      items={catalog.items}
      loading={catalog.loading}
      error={catalog.error}
      onRetry={() => catalog.reload()}
      onRequest={setFlowItem}
      onOpenRequest={openRequest}
    />
  ) : tab === 'requests' ? (
    <RequestsTab
      view={view}
      onViewChange={setView}
      items={requests.items}
      counts={requests.counts}
      loading={requests.loading}
      loadingMore={requests.loadingMore}
      hasMore={requests.hasMore}
      error={requests.error}
      busyKey={files.busyKey}
      onRetry={() => requests.reload()}
      onLoadMore={requests.loadMore}
      onOpen={item => openRequest(item.id)}
      onDownload={item =>
        item.generatedDocumentId &&
        files.issuedDocument({ id: item.generatedDocumentId, documentName: item.documentName }, 'save')
      }
      onNewRequest={() => setTab('request')}
    />
  ) : (
    <LockerTab
      items={locker.items}
      loading={locker.loading}
      admissionItems={locker.admissionItems}
      error={locker.error}
      admissionError={locker.admissionError}
      busyKey={files.busyKey}
      onRetry={() => locker.reload()}
      onView={doc => files.issuedDocument(doc, 'open')}
      onDownload={doc => files.issuedDocument(doc, 'save')}
      onViewAdmission={doc => files.admissionDocument(doc, 'open')}
      onDownloadAdmission={doc => files.admissionDocument(doc, 'save')}
      onOpenRequest={openRequest}
    />
  );

  return (
    <View style={scrollable ? styles.flex : undefined}>
      {tabBar}
      {scrollable ? (
        <ScrollView
          style={styles.flex}
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={refresh} colors={[colors.primary]} tintColor={colors.primary} />
          }>
          {body}
        </ScrollView>
      ) : (
        <View style={styles.webContent}>{body}</View>
      )}

      {!!studentId && (
        <RequestFlowModal
          visible={!!flowItem}
          item={flowItem}
          studentId={studentId}
          onClose={() => setFlowItem(null)}
          onSubmitted={() => {
            notify('Request sent to the school.', 'success');
            catalog.reload();
            requests.reload();
          }}
          onViewRequest={requestId => {
            setFlowItem(null);
            openRequest(requestId);
          }}
        />
      )}
    </View>
  );
}

// ── Request tab: the catalogue ──────────────────────────────────────────────

interface CatalogTabProps {
  studentName?: string;
  items: CatalogItem[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  onRequest: (item: CatalogItem) => void;
  onOpenRequest: (requestId: string) => void;
}

function CatalogTab({ studentName, items, loading, error, onRetry, onRequest, onOpenRequest }: CatalogTabProps) {
  const { colors, s } = useDocStyles();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<DocumentCategory | 'ALL'>('ALL');

  const categories = useMemo(() => catalogCategories(items), [items]);
  const shown = useMemo(() => filterCatalog(items, search, category), [items, search, category]);

  // A category that vanished after a child switch must not leave an empty filter behind.
  useEffect(() => {
    if (category !== 'ALL' && !categories.some(c => c.category === category)) setCategory('ALL');
  }, [categories, category]);

  if (loading && items.length === 0) return <CenteredLoader message="Loading documents..." />;

  return (
    <View>
      {!!studentName && (
        <Text style={s.intro}>
          Official documents you can request for <Text style={s.introStrong}>{studentName}</Text>.
        </Text>
      )}

      {!!error && <ErrorBanner message={error} onRetry={onRetry} />}

      {items.length > 0 && (
        <>
          <View style={styles.searchBox}>
            <FeatherIcon name="search" size={16} color={colors.textMuted} />
            <TextInput
              style={styles.searchInput}
              value={search}
              onChangeText={setSearch}
              placeholder="Search documents"
              placeholderTextColor={colors.textMuted}
              accessibilityLabel="Search documents"
              returnKeyType="search"
            />
            {!!search && (
              <TouchableOpacity onPress={() => setSearch('')} accessibilityLabel="Clear search" hitSlop={8}>
                <FeatherIcon name="x" size={16} color={colors.textMuted} />
              </TouchableOpacity>
            )}
          </View>

          {categories.length > 1 && (
            <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={s.chipRow}>
              {[{ category: 'ALL' as const, label: 'All' }, ...categories].map(option => {
                const active = option.category === category;
                return (
                  <TouchableOpacity
                    key={option.category}
                    style={[s.chip, active && s.chipActive]}
                    onPress={() => setCategory(option.category)}
                    accessibilityRole="tab"
                    accessibilityState={{ selected: active }}>
                    <Text style={[s.chipText, active && s.chipTextActive]}>{option.label}</Text>
                  </TouchableOpacity>
                );
              })}
            </ScrollView>
          )}
        </>
      )}

      {!error && items.length === 0 ? (
        <EmptyState
          icon="file"
          title="No documents to request"
          hint="The school has not published any documents for online requests yet."
        />
      ) : shown.length === 0 && items.length > 0 ? (
        <EmptyState icon="search" title="No documents match" hint="Try a different word or category." />
      ) : (
        shown.map(item => (
          <CatalogCard
            key={item.documentTypeId}
            item={item}
            onRequest={onRequest}
            onOpenRequest={onOpenRequest}
          />
        ))
      )}
    </View>
  );
}

// ── My Requests tab ─────────────────────────────────────────────────────────

interface RequestsTabProps {
  view: ParentRequestView;
  onViewChange: (view: ParentRequestView) => void;
  items: ParentRequestSummary[];
  counts: Record<string, number>;
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  error: string | null;
  busyKey: string | null;
  onRetry: () => void;
  onLoadMore: () => void;
  onOpen: (item: ParentRequestSummary) => void;
  onDownload: (item: ParentRequestSummary) => void;
  onNewRequest: () => void;
}

function RequestsTab({
  view,
  onViewChange,
  items,
  counts,
  loading,
  loadingMore,
  hasMore,
  error,
  busyKey,
  onRetry,
  onLoadMore,
  onOpen,
  onDownload,
  onNewRequest,
}: RequestsTabProps) {
  const { colors, s } = useDocStyles();

  return (
    <View>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={s.chipRow}>
        {REQUEST_VIEWS.map(option => {
          const active = option.value === view;
          const count = viewCount(counts, option.countKey);
          const urgent = option.value === 'ACTION_REQUIRED' && count > 0;
          return (
            <TouchableOpacity
              key={option.countKey}
              style={[
                s.chip,
                urgent && !active && { borderColor: colors.warning },
                active && s.chipActive,
              ]}
              onPress={() => onViewChange(option.value)}
              accessibilityRole="tab"
              accessibilityState={{ selected: active }}
              accessibilityLabel={`${option.label}, ${count}`}>
              <Text style={[s.chipText, urgent && !active && { color: colors.warning }, active && s.chipTextActive]}>
                {option.label} ({count})
              </Text>
            </TouchableOpacity>
          );
        })}
      </ScrollView>

      {!!error && <ErrorBanner message={error} onRetry={onRetry} />}

      {loading && items.length === 0 ? (
        <CenteredLoader message="Loading requests..." />
      ) : items.length === 0 ? (
        error ? null : (
          <EmptyState
            icon="inbox"
            title={emptyRequestsMessage(view)}
            hint={view === 'ACTION_REQUIRED' ? 'Requests the school sends back for changes appear here.' : undefined}
            actionLabel={view === 'ACTION_REQUIRED' ? undefined : 'Request a document'}
            onAction={view === 'ACTION_REQUIRED' ? undefined : onNewRequest}
          />
        )
      ) : (
        <>
          {items.map(item => (
            <RequestCard
              key={item.id}
              item={item}
              busy={!!item.generatedDocumentId && busyKey === `save:${item.generatedDocumentId}`}
              onOpen={onOpen}
              onDownload={onDownload}
            />
          ))}
          {hasMore && (
            <TouchableOpacity
              style={[s.outlineBtn, { alignSelf: 'center', marginBottom: 16 }, loadingMore && s.btnDisabled]}
              onPress={onLoadMore}
              disabled={loadingMore}
              accessibilityRole="button"
              accessibilityLabel="Load more requests">
              {loadingMore && <ActivityIndicator size="small" color={colors.primary} />}
              <Text style={s.outlineBtnText}>{loadingMore ? 'Loading...' : 'Load more'}</Text>
            </TouchableOpacity>
          )}
        </>
      )}
    </View>
  );
}

// ── My Documents tab: the family locker ─────────────────────────────────────

interface LockerTabProps {
  items: ParentDocument[];
  admissionItems: ParentAdmissionDocument[];
  loading: boolean;
  error: string | null;
  admissionError: string | null;
  busyKey: string | null;
  onRetry: () => void;
  onView: (doc: ParentDocument) => void;
  onDownload: (doc: ParentDocument) => void;
  onViewAdmission: (doc: ParentAdmissionDocument) => void;
  onDownloadAdmission: (doc: ParentAdmissionDocument) => void;
  onOpenRequest: (requestId: string) => void;
}

/**
 * Every document the family holds, in two groups: what the school has issued and what was
 * uploaded at admission. A chip per child narrows both groups at once.
 */
function LockerTab({
  items,
  admissionItems,
  loading,
  error,
  admissionError,
  busyKey,
  onRetry,
  onView,
  onDownload,
  onViewAdmission,
  onDownloadAdmission,
  onOpenRequest,
}: LockerTabProps) {
  const { s } = useDocStyles();
  const [childId, setChildId] = useState<string>('ALL');

  const children = useMemo(() => {
    const seen = new Map<string, string>();
    [...admissionItems, ...items].forEach(doc => {
      if (!seen.has(doc.studentId)) seen.set(doc.studentId, doc.studentName);
    });
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
  }, [items, admissionItems]);

  useEffect(() => {
    if (childId !== 'ALL' && !children.some(c => c.id === childId)) setChildId('ALL');
  }, [children, childId]);

  const forChild = <T extends { studentId: string }>(list: T[]) =>
    childId === 'ALL' ? list : list.filter(doc => doc.studentId === childId);
  const issued = forChild(items);
  const admission = forChild(admissionItems);

  if (loading && items.length === 0 && admissionItems.length === 0) {
    return <CenteredLoader message="Loading your documents..." />;
  }

  if (issued.length === 0 && admission.length === 0 && !error && !admissionError) {
    return (
      <EmptyState
        icon="folder"
        title="No documents yet"
        hint="Documents uploaded at admission and documents the school issues for your children are kept here."
      />
    );
  }

  return (
    <View>
      {children.length > 1 && (
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={s.chipRow}>
          {[{ id: 'ALL', name: 'All children' }, ...children].map(child => {
            const active = child.id === childId;
            return (
              <TouchableOpacity
                key={child.id}
                style={[s.chip, active && s.chipActive]}
                onPress={() => setChildId(child.id)}
                accessibilityRole="tab"
                accessibilityState={{ selected: active }}>
                <Text style={[s.chipText, active && s.chipTextActive]}>{child.name}</Text>
              </TouchableOpacity>
            );
          })}
        </ScrollView>
      )}

      <Text style={s.sectionTitle} accessibilityRole="header">
        Issued by the school
      </Text>
      {!!error && <ErrorBanner message={error} onRetry={onRetry} />}
      {issued.length === 0 && !error ? (
        <Text style={s.intro}>Certificates you request appear here once the school issues them.</Text>
      ) : (
        issued.map(doc => (
          <LockerCard
            key={doc.id}
            doc={doc}
            busyKey={busyKey}
            onView={onView}
            onDownload={onDownload}
            onOpenRequest={onOpenRequest}
          />
        ))
      )}

      <Text style={s.sectionTitle} accessibilityRole="header">
        Uploaded at admission
      </Text>
      {!!admissionError && <ErrorBanner message={admissionError} onRetry={onRetry} />}
      {admission.length === 0 && !admissionError ? (
        <Text style={s.intro}>No documents were uploaded at admission.</Text>
      ) : (
        admission.map(doc => (
          <AdmissionDocumentCard
            key={doc.id}
            doc={doc}
            busyKey={busyKey}
            onView={onViewAdmission}
            onDownload={onDownloadAdmission}
          />
        ))
      )}
    </View>
  );
}

const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    flex: { flex: 1 },
    scrollContent: { padding: Spacing.md, paddingBottom: Spacing.xxl },
    webContent: { paddingTop: Spacing.md },
    tabBarWrap: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: colors.surface,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    tabBar: { flex: 1, flexDirection: 'row' },
    tabButton: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      paddingVertical: 14,
      paddingHorizontal: 4,
      borderBottomWidth: 3,
      borderBottomColor: 'transparent',
    },
    tabButtonActive: { borderBottomColor: colors.primary },
    tabLabel: { fontSize: FontSize.sm, fontWeight: '700', flexShrink: 1 },
    tabLabelActive: { color: colors.primary },
    tabLabelInactive: { color: colors.textMuted },
    badge: {
      minWidth: 18,
      height: 18,
      borderRadius: 9,
      paddingHorizontal: 5,
      backgroundColor: colors.warning,
      alignItems: 'center',
      justifyContent: 'center',
    },
    badgeText: { fontSize: 10, fontWeight: '800', color: '#FFFFFF' },
    refreshBtn: { paddingHorizontal: 14, paddingVertical: 12 },
    searchBox: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: Spacing.sm,
      backgroundColor: colors.surface,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: BorderRadius.sm,
      paddingHorizontal: Spacing.md,
      marginBottom: Spacing.md,
    },
    searchInput: { flex: 1, paddingVertical: 11, fontSize: FontSize.md, color: colors.textPrimary },
  });
